import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Dropdown, Avatar, message } from 'antd'
import { getTempEnvParam } from 'env'

import { UserOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { getSearchParams, useIsMobile, lockSafebox } from 'utils'
import { isUS, isJP, isKR } from 'conf'
import { store as reduxStore } from 'index'
import ProfilePhoto from 'components/ProfilePhoto'
import { getInitialAvatar, logoutConfirm, GridIcon } from 'utils/ui'
import Settings from 'popups/Settings'
import handleBulkExport from 'utils/importExport/bulkExport'
import {
  openImportFileWithSource,
  processImportData,
  convertWithMapping,
} from 'utils/importExport/bulkImport'
import ImportMappingModal from 'utils/importExport/ImportMappingModal'
import ImportSourceModal from 'utils/importExport/ImportSourceModal'
import ImportV2Modal from 'utils/importExport/ImportV2Modal'
import { featureFlags } from 'config/feature-flags'

import { SERVICE_NAME_TITLE, SERVICE_LOCK_BOX } from 'service'
import { IMPORT_EXPORT_CONFIG } from 'pages/records/components/helper'
import avatarIcon from '@/assets/images/avatar_default.png'
import arrowDown from '@/assets/images/cheveron-down-24.svg'
import zeroDarkWebIcon from '@/assets/images/service/service-zero-darkweb.svg'

import styles from './style.module.scss'

export const getHelpCenterLink = () => {
  if (isKR()) {
    return 'https://docs.officepassword.kr'
  }

  if (isUS()) {
    return 'https://docs.safepassword.io'
  }

  if (isJP()) {
    return 'https://docs.safepassword.jp'
  }

  return 'https://docs.safepassword.kr'
}

const ProfileMenu = () => {
  const user = useSelector((state) => state.user)
  const settings = useSelector((state) => state.settings)
  const { zeroDarkWebSettings } = settings
  const zdwCount = useSelector((state) => state.user.leakedCount)

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [importV2Visible, setImportV2Visible] = useState(false)
  // v1 경로(featureFlags.importV2 === false일 때 사용)
  const [importSourceVisible, setImportSourceVisible] = useState(false)
  const [mappingModalData, setMappingModalData] = useState(null)
  const [messageApi, contextHolder] = message.useMessage()
  const isMobile = useIsMobile()
  const { t } = useTranslation()

  const logout = (e) => {
    e.preventDefault()
    logoutConfirm()
  }

  const openSettings = () => {
    setSettingsOpen(true)
  }

  const handleImport = () => {
    if (featureFlags.importV2) {
      setImportV2Visible(true)
    } else {
      setImportSourceVisible(true)
    }
  }

  // v1 경로 핸들러 (featureFlags.importV2 === false일 때 사용)
  const handleSourceSelect = (source) => {
    setImportSourceVisible(false)
    openImportFileWithSource(source, t, messageApi)
  }

  const handleMappingConfirm = async (finalMapping) => {
    if (!mappingModalData) return
    const { rows } = mappingModalData
    const { records, skippedRows } = convertWithMapping(rows, finalMapping)
    setMappingModalData(null)
    await processImportData(records, t, messageApi, { skippedRows })
  }

  const isMac = navigator.platform.toUpperCase().includes('MAC')
  const modifierKey = isMac ? 'Cmd' : 'Ctrl'
  const items = [
    {
      key: 'settings',
      label: <a type="button">{t('topBar.profileMenu.settings')}</a>,
      onClick: openSettings,
    },
    (IMPORT_EXPORT_CONFIG.enableExport || IMPORT_EXPORT_CONFIG.enableImport) && {
      type: 'divider',
    },
    IMPORT_EXPORT_CONFIG.enableExport && {
      key: 'export',
      label: t('topBar.profileMenu.export'),
      popupClassName: 'floatingSubMenu',
      children: [
        {
          key: 'export-my',
          label: t('topBar.profileMenu.exportMyPasswords'),
          onClick: () => handleBulkExport('my', t, messageApi),
        },
        {
          key: 'export-shared',
          label: t('topBar.profileMenu.exportSharedPasswords'),
          onClick: () => handleBulkExport('shared', t, messageApi),
        },
        {
          key: 'export-all',
          label: t('topBar.profileMenu.exportAllPasswords'),
          onClick: () => handleBulkExport('all', t, messageApi),
        },
      ],
    },
    IMPORT_EXPORT_CONFIG.enableImport && {
      key: 'import',
      label: <a>{t('topBar.profileMenu.import')}</a>,
      onClick: handleImport,
    },
    (IMPORT_EXPORT_CONFIG.enableExport || IMPORT_EXPORT_CONFIG.enableImport) && {
      type: 'divider',
    },
    {
      label: (
        <div className={styles.lockSafeboxItem}>
          <a>{t(SERVICE_LOCK_BOX, { service: t(SERVICE_NAME_TITLE) })}</a>
          <span className={styles.shortKey}>{modifierKey}+Shift+L</span>
        </div>
      ),
      key: 'lock',
      onClick: lockSafebox,
    },
    {
      type: 'divider',
    },
    {
      label: (
        <div className={styles.lockSafeboxItem}>
          <a>{t('topBar.profileMenu.helpCenter')}</a>
        </div>
      ),
      key: 'helpCenter',
      onClick: () => {
        window.open(getHelpCenterLink(), '_blank')
      },
    },
    {
      type: 'divider',
    },
    {
      label: (
        <a href="#" onClick={logout}>
          {t('topBar.profileMenu.logout')}
        </a>
      ),
      key: 'logout',
    },
  ].filter(Boolean)

  let avatarElem = null
  if (user == null) {
    avatarElem = <UserOutlined />
  } else if (user.avatar == null || user.avatar.trim().length === 0) {
    avatarElem = getInitialAvatar(user.name, user.email)
  } else {
    avatarElem = <ProfilePhoto className="avatar-img" url={user.avatar} displayName={user.name} />
  }

  const handleZeroDarkWebIconClick = () => {
    const tablePage = getTempEnvParam('curTablePage')

    if (settings.showZeroDarkWeb) {
      const { leaked } = getSearchParams()
      if (leaked === 'true') {
        reduxStore.dispatch({
          type: 'settings/SET_IS_LEAKED',
          payload: { isLeaked: false },
        })
      }
    }

    reduxStore.dispatch({
      type: 'settings/CHANGE_SETTING',
      payload: {
        setting: 'showZeroDarkWeb',
        value: !settings.showZeroDarkWeb,
      },
    })

    if (settings.showZeroDarkWeb === true) {
      tablePage.reqData(null)
    }
  }

  const dropDownProps = {
    menu: { items },
    popupStyle: { width: 120 },
    trigger: ['click'],
    overlayClassName: classNames(styles.userMenu, 'contextMenu'),
    placement: 'bottomRight',
  }

  const changeGridState = () => {
    window.dispatchEvent(new CustomEvent('gridStateChanged'))
  }

  const isRecordPage = window.location.pathname.includes('/records')
  const isZdwActive =
    zeroDarkWebSettings?.zdw_active === 'true' || zeroDarkWebSettings?.zdw_active === true

  return (
    <div className={styles.rightTopBar}>
      {contextHolder}
      {featureFlags.importV2 ? (
        <ImportV2Modal
          visible={importV2Visible}
          onClose={() => setImportV2Visible(false)}
          t={t}
          messageApi={messageApi}
        />
      ) : (
        <>
          <ImportSourceModal
            visible={importSourceVisible}
            onSelect={handleSourceSelect}
            onCancel={() => setImportSourceVisible(false)}
            t={t}
          />
          {mappingModalData && (
            <ImportMappingModal
              visible={!!mappingModalData}
              columns={mappingModalData.columns}
              rows={mappingModalData.rows}
              totalCount={mappingModalData.totalCount}
              onConfirm={handleMappingConfirm}
              onCancel={() => setMappingModalData(null)}
              t={t}
            />
          )}
        </>
      )}
      {isZdwActive && window.location.pathname.startsWith('/records') && (
        <div
          className={styles.zeroDarkWebIconContainer}
          onClick={handleZeroDarkWebIconClick}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleZeroDarkWebIconClick(e)
            }
          }}
        >
          <img src={zeroDarkWebIcon} alt="zero-dark-web" className={styles.zeroDarkWebIcon} />
          {zdwCount > 0 && <span className={styles.leakedBadge}>{zdwCount}</span>}
        </div>
      )}
      {isRecordPage && isMobile && <GridIcon isGrid={settings.isGrid} func={changeGridState} />}
      {!isMobile && (
        <React.Fragment>
          <span className={styles.workspaceName}>{user.workspace?.workspace_name}</span>
        </React.Fragment>
      )}
      <div className={styles.userInfo}>
        {/* {isMobile ? null : <span className={styles.userName}>{user.name}</span>} */}
        <Dropdown {...dropDownProps}>
          {isMobile ? (
            <Avatar
              src={user.avatar || avatarIcon}
              className={styles.avatar}
              shape="circle"
              size={32}
              icon={avatarElem}
            />
          ) : (
            <div className={styles.userInfo}>
              <Avatar
                src={user.avatar || avatarIcon}
                className={styles.avatar}
                shape="circle"
                size={32}
                icon={avatarElem}
              />
              <span className={styles.userName}>{user.name}</span>
              <div className={styles.dropdown}>
                <img src={arrowDown} alt="arrow-down" />
              </div>
            </div>
          )}
        </Dropdown>
        <Settings
          open={settingsOpen}
          onClose={() => {
            setSettingsOpen(false)
          }}
          setSettingsOpen={setSettingsOpen}
        />
      </div>
    </div>
  )
}

export default ProfileMenu
